/**
 * Die Recherche-Gruppe — eine Frage, ein zugeklappter Block statt Dutzender Mail-Karten. (21.10.2026)
 *
 * Martin, 07:45: *„Die Ausgabe nur wegen einer E-Mail-Suche ist irre! Das kann kein Mensch lesen. Eine Frage, soll eine Antwort
 * kommen, kein Schwein interessieren diese ganzen Störgeräusche.“* Für EINE Frage standen „E-Mail-Suche · 3-mal“ (drei volle
 * Trefferlisten, dieselben Mails mehrfach), „E-Mail gelesen · 3-mal“ (drei ganze Mails) und zwei Anhang-Karten untereinander.
 *
 * ## Die Regel
 *
 * - Recherche-Karten sind die lesenden Mail-Karten: Suche (gmail_search), gelesene Mail (gmail_read_message) und die Anhang-Karte
 *   (Familie „mail“ aus der Anzeige, gmail_anhang_lesen).
 * - Ab ZWEI solcher Karten in EINEM Zug wandern sie in einen einzigen `<details class="zug-recherche">`, standardmäßig ZU
 *   („Recherche: 3 Suchen, 3 Mails gelesen, 2 Anhänge“). Eine einzelne Karte bleibt offen sichtbar, wie bisher.
 * - Innen: dieselbe Mail steht nur einmal (die spätere Zeile fällt weg, die erste nennt „In Suche 1, 3“); „Lesemodus“ und
 *   „Ganzen Text lesen: …“ erscheinen einmal je Gruppe. Die Karten und ihre Knöpfe bleiben dieselben Elemente, nur verschoben.
 * - Ein Fehler klappt den Block auf (Fehler dürfen nicht zugeklappt liegen).
 *
 * ## Die Ausnahme (kleinste sinnvolle Regel)
 *
 * Ist die LETZTE Karte des Zuges eine gelesene Mail und beziehen sich alle anderen Karten auf dieselbe Mail (Suche, in deren
 * Treffern sie steht; dieselbe Mail nochmals gelesen; Anhang-Karte), dann ist diese Mail die Antwort des Zuges: sie bleibt als
 * eigene Karte offen UNTER dem Block, der den Rest zuklappt („Zeig mir die Mail von …“: Suche + Lesen).
 *
 * Alles wird bei JEDEM Anhängen aus dem DOM neu gerechnet (live und beim Neuaufbau aus dem Verlauf dasselbe Ergebnis, kein Zähler).
 * Der Server und die Kennungen der Ergebnisse bleiben unberührt.
 */
import { ART_KLASSE } from "./karte.js";
import { FALTE_KLASSE, ZUG_FENSTER_MS, haengeKarteAn } from "./schritte.js";

export const RECHERCHE_KLASSE = "zug-recherche";
const RECHERCHE_SCHLUESSEL = "recherche";
const ANHANG_SCHLUESSEL = "familie:mail";
const RECHERCHE_WERKZEUGE: ReadonlySet<string> = new Set(["gmail_search", "gmail_read_message", ANHANG_SCHLUESSEL, "gmail_anhang_lesen"]);

/** Wie haengeKarteAn — und danach bündelt der Zug seine Recherche-Karten. Die eine Tür für app.ts. */
export function haengeRechercheKarteAn(behaelter: HTMLElement, karte: HTMLElement, werkzeug: string): void {
  haengeKarteAn(behaelter, karte, werkzeug);
  buendleRecherche(behaelter);
  buendleProzess(behaelter);
}

/** Die Recherche-Karten eines Zuges in Dokumentreihenfolge — ohne Schritt-Zeilen, Faltungen und den Block selbst. */
function rechercheKarten(behaelter: HTMLElement): HTMLElement[] {
  return Array.from(behaelter.querySelectorAll<HTMLElement>(".tool-card[data-werkzeug]")).filter((k) =>
    RECHERCHE_WERKZEUGE.has(k.dataset.werkzeug ?? ""),
  );
}

const ids = (k: HTMLElement): string[] => (k.dataset.mailIds ?? "").split(" ").filter(Boolean);

/** Die Mail, die der Zug als Antwort zeigt: die letzte Karte, wenn alle anderen sich auf dieselbe Mail beziehen. */
function antwortMail(karten: HTMLElement[]): HTMLElement | null {
  const letzte = karten[karten.length - 1];
  const id = letzte?.dataset.werkzeug === "gmail_read_message" ? letzte.dataset.mailId : undefined;
  if (!letzte || !id) return null;
  const gleich = karten.slice(0, -1).every((k) => {
    const w = k.dataset.werkzeug;
    if (w === "gmail_search") return ids(k).includes(id);
    if (w === "gmail_read_message") return k.dataset.mailId === id;
    return istAnhang(k);
  });
  return gleich ? letzte : null;
}

/** Die Anhang-Karte: die Familienkarte der Anzeige, oder (ältere Verläufe) die einzelne Karte des Werkzeugs. */
function istAnhang(k: HTMLElement): boolean {
  return k.dataset.werkzeug === ANHANG_SCHLUESSEL || k.dataset.werkzeug === "gmail_anhang_lesen";
}

function zaehlWort(n: number, eins: string, viele: string): string {
  return `${n} ${n === 1 ? eins : viele}`;
}

/** „Recherche: 3 Suchen, 3 Mails gelesen, 2 Anhänge“ — nur, was im Block steckt. */
function kopfText(karten: HTMLElement[]): string {
  const suchen = karten.filter((k) => k.dataset.werkzeug === "gmail_search").length;
  const mails = karten.filter((k) => k.dataset.werkzeug === "gmail_read_message").length;
  const anhaenge = karten
    .filter(istAnhang)
    .reduce((n, k) => n + (k.querySelectorAll(":scope > .tool-list > li").length || 1), 0);
  const teile = [
    suchen ? zaehlWort(suchen, "Suche", "Suchen") : "",
    mails ? `${zaehlWort(mails, "Mail", "Mails")} gelesen` : "",
    anhaenge ? zaehlWort(anhaenge, "Anhang", "Anhänge") : "",
  ].filter(Boolean);
  return `Recherche: ${teile.join(", ")}`;
}

/** Dieselbe Mail nur einmal: die erste Zeile bleibt und nennt ihre Suchen, spätere Wiederholungen werden verborgen. */
function entdoppeln(suchen: HTMLElement[]): void {
  const gesehen = new Map<string, { li: HTMLElement; nummern: number[] }>();
  suchen.forEach((karte, i) => {
    const eintraege = Array.from(karte.querySelectorAll<HTMLElement>("li[data-mail-id]"));
    for (const li of eintraege) {
      const id = li.dataset.mailId ?? "";
      const frueher = gesehen.get(id);
      li.hidden = frueher !== undefined;
      if (frueher) frueher.nummern.push(i + 1);
      else gesehen.set(id, { li, nummern: [i + 1] });
    }
    karte.hidden = eintraege.length > 0 && eintraege.every((li) => li.hidden);
  });
  for (const { li, nummern } of gesehen.values()) {
    li.querySelector(":scope > .tool-mail-suchbezug")?.remove();
    if (nummern.length < 2) continue;
    const bezug = document.createElement("p");
    bezug.className = "tool-mail-suchbezug";
    bezug.textContent = `In Suche ${nummern.join(", ")}`;
    li.querySelector(":scope > .tool-list-row")?.after(bezug);
  }
}

/** Von mehreren gleichen Bausteinen bleibt der erste sichtbare; alle weiteren sind verborgen. */
function nurEinmal(karten: HTMLElement[], auswahl: string): void {
  let schonDa = false;
  for (const k of karten) {
    const baustein = k.querySelector<HTMLElement>(`:scope > ${auswahl}`);
    if (!baustein) continue;
    baustein.hidden = k.hidden || schonDa;
    if (!baustein.hidden) schonDa = true;
  }
}

/** Das Kind des Behälters, in dem `el` steckt (die Karte selbst oder ihre Faltung). */
function oberstes(behaelter: HTMLElement, el: Element): Element {
  let k: Element = el;
  while (k.parentElement && k.parentElement !== behaelter) k = k.parentElement;
  return k;
}

/** Bündelt die Recherche-Karten des Zuges, wenn es mindestens zwei sind. Idempotent. */
export function buendleRecherche(behaelter: HTMLElement): void {
  const alle = rechercheKarten(behaelter);
  let block = behaelter.querySelector<HTMLDetailsElement>(`:scope > .${RECHERCHE_KLASSE}:not(.zug-prozess)`);
  if (alle.length < 2 && !block) return;
  const antwort = antwortMail(alle);
  const innen = alle.filter((k) => k !== antwort);
  if (!block) {
    block = document.createElement("details");
    block.className = RECHERCHE_KLASSE;
    block.dataset.werkzeug = RECHERCHE_SCHLUESSEL;
    const kopf = document.createElement("summary");
    kopf.className = "zug-recherche-kopf";
    block.appendChild(kopf);
    // An die Stelle der ersten Karte (oder ihrer Faltung).
    oberstes(behaelter, alle[0]).before(block);
  }
  for (const k of innen) {
    // Was eine Faltung versteckt oder verstummt hat, zeigt der Block wieder.
    k.hidden = false;
    const titel = k.querySelector<HTMLElement>(":scope > .tool-card-title");
    if (titel) titel.hidden = false;
    for (const art of k.querySelectorAll<HTMLElement>(`.${ART_KLASSE}`)) art.hidden = false;
    block.appendChild(k);
  }
  if (antwort) {
    antwort.hidden = false;
    block.after(antwort);
  }
  // Leere Faltungen (samt „und N weitere“) räumen.
  for (const falte of Array.from(behaelter.querySelectorAll<HTMLElement>(`:scope > .${FALTE_KLASSE}`))) {
    if (!falte.querySelector("[data-werkzeug]")) falte.remove();
  }
  const kopf = block.querySelector<HTMLElement>(":scope > .zug-recherche-kopf");
  if (kopf) kopf.textContent = kopfText(innen);
  entdoppeln(innen.filter((k) => k.dataset.werkzeug === "gmail_search"));
  // Ein Schalter und ein Hinweis je Gruppe — die Antwortkarte (sichtbar vor dem Block) geht vor.
  const reihe = antwort ? [antwort, ...innen] : innen;
  nurEinmal(reihe, ".tool-mail-lesemodus");
  nurEinmal(reihe, ".tool-mail-ganz-hinweis");
  // Ein Fehler darf nicht zugeklappt liegen.
  if (innen.some((k) => k.classList.contains("tool-error"))) block.open = true;
}

/*
 * ## Die Prozess-Gruppe (27.10.2026) — dieselbe Hülle für Browser-Karten
 *
 * Martin, 13:53, 18 Karten für EINEN Browser-Prozess: „Das sind so viele Informationen, obwohl es um einen Prozess geht. Es sollte
 * geschlossen und sauber sein.“ Ab ZWEI Karten mit Werkzeugnamen `browser_*` in einem Zug stehen sie in
 * EINEM `<details class="zug-recherche zug-prozess">`, zu: „Browser · 18 Schritte“, darunter als Untertitel der letzte Schritt.
 * Karten mit Entscheidung (Ja-Knopf/Vorschlag, jeder Knopf) und Fehlerkarten bleiben SICHTBAR außerhalb; die Gruppe selbst ist ein
 * natives details/summary (Tastatur, aria-expanded vom Browser). Wie oben: bei jedem Anhängen aus dem DOM neu gerechnet.
 */
const PROZESS_SCHLUESSEL = "prozess-browser";
/** Alle Werkzeuge `browser_*` (open_url, get_page_text, screenshot, click, ablauf, upload …) gehören zu EINEM Prozess. */
const PROZESS_PRAEFIX = "browser_";

function istProzessKarte(k: HTMLElement): boolean {
  if (!(k.dataset.werkzeug ?? "").startsWith(PROZESS_PRAEFIX)) return false;
  if (k.classList.contains("tool-error") || k.dataset.vorschlag === "ja") return false;
  // Eine Karte mit Knopf ist eine Entscheidung — sie bleibt sichtbar (die Faltungs-Knöpfe „und N weitere“ zählen nicht).
  return !k.querySelector("button:not(.tool-card-weitere)");
}

/** Der letzte Schritt als Untertitel: Titel der Karte, sonst ihr erster Text — gekürzt, nur textContent. */
function schrittUntertitel(k: HTMLElement): string {
  const roh = (k.querySelector(":scope > .tool-card-title")?.textContent || k.textContent || "").replace(/\s+/g, " ").trim();
  return roh.length > 80 ? `${roh.slice(0, 79)}…` : roh;
}

/** Bündelt die Browser-Karten des Zuges, wenn es mindestens zwei sind. Idempotent. */
export function buendleProzess(behaelter: HTMLElement): void {
  const alle = Array.from(behaelter.querySelectorAll<HTMLElement>(".tool-card[data-werkzeug]")).filter(istProzessKarte);
  let block = behaelter.querySelector<HTMLDetailsElement>(`:scope > .${RECHERCHE_KLASSE}[data-werkzeug="${PROZESS_SCHLUESSEL}"]`);
  if (alle.length < 2 && !block) return;
  if (!block) {
    block = document.createElement("details");
    block.className = `${RECHERCHE_KLASSE} zug-prozess`;
    block.dataset.werkzeug = PROZESS_SCHLUESSEL;
    const kopf = document.createElement("summary");
    kopf.className = "zug-recherche-kopf";
    const titel = document.createElement("span");
    titel.className = "zug-prozess-titel";
    const sub = document.createElement("span");
    sub.className = "zug-prozess-untertitel";
    kopf.append(titel, sub);
    block.appendChild(kopf);
    oberstes(behaelter, alle[0]).before(block);
  }
  for (const k of alle) {
    k.hidden = false;
    const titel = k.querySelector<HTMLElement>(":scope > .tool-card-title");
    if (titel) titel.hidden = false;
    for (const art of k.querySelectorAll<HTMLElement>(`.${ART_KLASSE}`)) art.hidden = false;
    block.appendChild(k);
  }
  for (const falte of Array.from(behaelter.querySelectorAll<HTMLElement>(`:scope > .${FALTE_KLASSE}`))) {
    if (!falte.querySelector("[data-werkzeug]")) falte.remove();
  }
  const innen = Array.from(block.querySelectorAll<HTMLElement>(":scope > .tool-card[data-werkzeug]"));
  const titel = block.querySelector<HTMLElement>(".zug-prozess-titel");
  const sub = block.querySelector<HTMLElement>(".zug-prozess-untertitel");
  if (titel) titel.textContent = `Browser · ${zaehlWort(innen.length, "Schritt", "Schritte")}`;
  if (sub) sub.textContent = innen.length ? schrittUntertitel(innen[innen.length - 1]) : "";
}

/**
 * Die Antwort zuerst: der Zug mit einem Recherche-Block rückt hinter Vesperas Antwortblase. (21.10.2026, Martin)
 *
 * Die Antwort kommt nach den Werkzeugen und stünde sonst ÜBER dem Block. Gesucht wird die letzte Vespera-Blase zwischen dem
 * Zug und der nächsten Nutzer-Blase; der Zug (Behälter mit dem Block) steht danach direkt unter ihr. Ohne Antwort bleibt er
 * stehen, ebenso jeder Zug ohne Block (Einzelkarten behalten ihren Platz). Idempotent und nur vom DOM abhängig — live und im
 * Neuaufbau dasselbe. Der Zeitstempel des Zuges zieht mit, damit spätere Einordnung nach Zeit (insertOrdered) nicht darüber fällt.
 */
export function ordneRechercheNachAntwort(chat: Element): void {
  for (const block of Array.from(chat.querySelectorAll(`:scope > .chat-zug > .${RECHERCHE_KLASSE}`))) {
    const zug = block.parentElement;
    if (!zug) continue;
    // Nur die Vespera-Blase(n) DIREKT im Anschluss, binnen des Zug-Fensters: ein Tagestrenner, ein anderer Zug, eine
    // Nutzer-Blase oder eine Blase vom Folgetag beendet die Suche. Flüchtiges (Warteblase) wird übersprungen.
    const zugTs = Number(zug.dataset.ts ?? 0);
    let antwort: HTMLElement | null = null;
    for (let s = zug.nextElementSibling; s; s = s.nextElementSibling) {
      if (s.classList.contains("is-pending")) continue;
      if (!s.classList.contains("bubble") || !s.classList.contains("bot") || s.classList.contains("is-placeholder")) break;
      if (Math.abs(Number((s as HTMLElement).dataset.ts ?? 0) - zugTs) >= ZUG_FENSTER_MS) break;
      antwort = s as HTMLElement;
    }
    if (!antwort) continue;
    antwort.after(zug);
    const ts = Number(antwort.dataset.ts ?? 0);
    if (ts > Number(zug.dataset.ts ?? 0)) zug.dataset.ts = String(ts);
  }
}

let ordnungGeplant = false;
/** Wie die Tagestrenner: im Neuaufbau viele Einfügungen, EIN Durchgang danach. */
export function planeRechercheOrdnung(chat: Element): void {
  if (ordnungGeplant) return;
  ordnungGeplant = true;
  queueMicrotask(() => {
    ordnungGeplant = false;
    ordneRechercheNachAntwort(chat);
  });
}
